import { safeGetJSON, safeSetJSON } from "tap/utils/safeLocalStorage.js";

export const DEFAULT_PAGE_SIZE_OPTIONS = [5, 10, 25, 50, 100];
export const DEFAULT_ITEMS_PER_PAGE = 10;

export const PAGINATION_STORAGE_KEYS = {
  licensingJobs: "licensingJobsItemsPerPage",
  licensingJobsJobView: "licensingJobsJobViewItemsPerPage",
  tapJobs: "tapJobsItemsPerPage",
  tapJobsJobView: "tapJobsJobViewItemsPerPage",
  tapJobsTasks: "tapJobsTasksItemsPerPage",
  updateEngine: "updateEngineItemsPerPage",
  updateEngineJobView: "updateEngineJobViewItemsPerPage",
  windows: "windowsItemsPerPage",
  users: "usersItemsPerPage",
  rateCards: "rateCardsItemsPerPage",
  tprExceptions: "tprExceptionsItemsPerPage",
  pmxPricesAndDates: "pmxPricesAndDatesItemsPerPage",
  pmxDeliveries: "pmxDeliveriesItemsPerPage",
  offers: "offersItemsPerPage",
};

const LEGACY_STORAGE_KEYS = {
  licJobViewItemsPerPage: PAGINATION_STORAGE_KEYS.licensingJobsJobView,
};

/**
 * Returns a persisted page size for a specific component.
 * @param {string} storageKey - localStorage key for the component
 * @param {number} [fallback=DEFAULT_ITEMS_PER_PAGE]
 * @returns {number}
 */
export function getStoredItemsPerPage(
  storageKey,
  fallback = DEFAULT_ITEMS_PER_PAGE,
) {
  let saved = safeGetJSON(storageKey, null);

  if (saved === null) {
    const legacyKey = Object.keys(LEGACY_STORAGE_KEYS).find(
      (key) => LEGACY_STORAGE_KEYS[key] === storageKey,
    );
    if (legacyKey) {
      const legacyValue = safeGetJSON(legacyKey, null);
      if (
        legacyValue !== null &&
        DEFAULT_PAGE_SIZE_OPTIONS.includes(legacyValue)
      ) {
        saved = legacyValue;
        safeSetJSON(storageKey, legacyValue);
      }
    }
  }

  if (saved === null) {
    return fallback;
  }

  return DEFAULT_PAGE_SIZE_OPTIONS.includes(saved) ? saved : fallback;
}

/**
 * Persists a page size preference for a specific component.
 * @param {string} storageKey
 * @param {number} value
 * @returns {boolean}
 */
export function persistItemsPerPage(storageKey, value) {
  if (!DEFAULT_PAGE_SIZE_OPTIONS.includes(value)) {
    return false;
  }
  return safeSetJSON(storageKey, value);
}

/**
 * Persists page-size fields from a Redux update payload using a key map.
 * @param {Object} payload
 * @param {Object<string, string>} keyMap - Maps payload field names to storage keys
 */
export function persistItemsPerPageFromPayload(payload, keyMap) {
  if (!payload || typeof payload !== "object" || !keyMap) {
    return;
  }

  Object.entries(keyMap).forEach(([payloadKey, storageKey]) => {
    if (payload[payloadKey] !== undefined) {
      persistItemsPerPage(storageKey, payload[payloadKey]);
    }
  });
}

/**
 * Returns the default zero-based pagination range tuple for a page size.
 * @param {number} itemsPerPage
 * @returns {[number, number]}
 */
export function getInitialPaginationRange(itemsPerPage) {
  return [0, itemsPerPage - 1];
}

/**
 * Preserves component-specific pagination preferences when resetting slice state.
 * @param {Object} state - Current slice state
 * @param {Object} nextState - Reset state being returned
 * @param {string[]} [rangeKeys=[]] - Range fields to reset for the preserved page size
 * @param {string[]} [fields=["itemsPerPage"]] - State fields to preserve
 * @returns {Object}
 */
export function withPreservedPagination(
  state,
  nextState,
  rangeKeys = [],
  fields = ["itemsPerPage"],
) {
  const preserved = {};

  fields.forEach((field) => {
    if (state[field] !== undefined) {
      preserved[field] = state[field];
    }
  });

  const rangeItemsPerPage = preserved.itemsPerPage ?? state.itemsPerPage;
  if (rangeItemsPerPage && rangeKeys.length > 0) {
    const range = getInitialPaginationRange(rangeItemsPerPage);
    rangeKeys.forEach((key) => {
      preserved[key] = range;
    });

    if ("defaultFilterRange" in nextState) {
      preserved.defaultFilterRange = range;
    }
  }

  return { ...nextState, ...preserved };
}
